<template>
  <BlBackdrop
    title="Edit details"
    label="Update"
    :submitLoading="submitLoading"
    :disabled="isDisabledSubmit"
    @backdrop-close="$emit('close-modal')"
    @backdrop-action="onSubmit"
  >
    <div
      v-if="modalLoading"
      class="grid items-center justify-center gap-4 px-8 h-100"
      :style="{ width: '48rem' }"
    >
      <div class="blum-loader"></div>
    </div>
    <div
      v-else
      class="grid items-center grid-cols-2 gap-4 px-8"
      :style="{ width: '48rem' }"
    >
      <BlInputText
        class="col-span-2"
        :input="nameInput"
        @changed="inputHandler"
      />
      <BlSelectBox
        label="Curriculum"
        asterisk
        search
        v-model="curriculumIdInput"
        placeholder="Select curriculum"
        :options="[
          {
            value: target.curriculum_id,
            label: target.curriculum_name,
            prefixColor: target.curriculum_color
          }
        ]"
        :fetchProperty="{
          url: `/api/v1/curriculums?sort=name_asc&query=`,
          dataKey: 'curriculums',
          countKey: 'total_count',
          optionKeys: { value: 'id', label: 'name', prefixColor: 'color' }
        }"
        @change="curriculumIdInput = $event"
      />
      <!--  -->
      <!-- <BlSelectBox
        label="Data collection method"
        asterisk
        v-model="typeInput"
        placeholder="Select type"
        :options="typeOptions"
        disabled
        @change="typeInput = $event"
      /> -->
      <BlSelectBox
        v-if="!panelId"
        label="Status"
        asterisk
        v-model="statusInput"
        placeholder="Select status"
        :options="statusOptions"
        @change="statusInput = $event"
      />
      <!--  -->
      <BlInputDate
        v-if="!panelId"
        :input="dateIntroduceInput"
        @changed="inputHandler"
      />
      <BlInputDate
        v-if="!panelId"
        :input="dateMasteredInput"
        @changed="inputHandler"
      />
      <!--  -->
      <BlInputText
        class="col-span-2"
        :input="descriptionInput"
        @changed="inputHandler"
      />
      <!--  -->
      <BlInputText
        v-if="target.type.includes('Pir')"
        :input="intervalInput"
        @changed="inputHandler"
      />
      <BlInputText
        v-if="target.type.includes('Pir')"
        :input="durationInput"
        @changed="inputHandler"
      />
      <!--  -->
      <BlInputText
        v-if="target.type.includes('Percentage') || target.type.includes('Pir')"
        :input="goalInput"
        @changed="inputHandler"
      />
      <BlInputTime
        v-if="target.type.includes('Duration')"
        :input="timeInput"
        @changed="inputHandler"
      />
      <BlInputText
        v-if="target.type.includes('Frequency')"
        :input="goalFrequencyInput"
        @changed="inputHandler"
      />
      <div
        v-if="target.type.includes('Prompting')"
        class="flex flex-col col-span-2 gap-4 pt-8"
      >
        <div class="flex flex-col gap-2">
          <BlTextSubtitle1 text="Goal and success metric" />
          <BlTextBody2
            v-if="promptingFormatInput === 'classic'"
            text="Mark this target as successful when the client uses a specific prompt at least a certain number of times."
          />
          <BlTextBody2
            v-if="promptingFormatInput === 'custom'"
            text="The target passes if the client's score meets the goal you set."
          />
        </div>
        <div
          v-if="promptingFormatInput === 'classic'"
          class="grid grid-cols-2 gap-4"
        >
          <BlSelectBox
            :key="`success-metric-${JSON.stringify(defaultPrompts)}`"
            label="Prompt level"
            asterisk
            placeholder="Select prompt"
            v-model="successMetricInput"
            :options="
              defaultPrompts.map(i => ({ value: i.name, label: i.name }))
            "
            drop="up"
            position="top"
            @change="successMetricInput = $event"
          />
          <BlInputText :input="goalPromptInput" @changed="inputHandler" />
        </div>
        <div
          v-if="promptingFormatInput === 'custom'"
          class="grid grid-cols-2 gap-4"
        >
          <BlInputText :input="goalInput" @changed="inputHandler" />
          <BlSelectBox
            label="Success metric"
            asterisk
            v-model="successMetricInput"
            placeholder="Select success metric"
            :options="successMetricOptions"
            @change="successMetricInput = $event"
          />
        </div>
      </div>
      <BlSelectBox
        v-else-if="!target.type.includes('Sbt')"
        label="Success metric"
        asterisk
        v-model="successMetricInput"
        placeholder="Select success metric"
        :options="successMetricOptions"
        @change="successMetricInput = $event"
      />
      <BlInputText
        v-if="target.type.includes('Percentage')"
        :input="trialsInput"
        @changed="inputHandler"
      />
    </div>
  </BlBackdrop>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

import BlBackdrop from "../../../BlBackdrop.vue";
import BlTextSubtitle1 from "../../../BlTextSubtitle1.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlInputText from "../../../BlInputText.vue";
import BlInputDate from "../../../BlInputDate.vue";
import BlInputTime from "../../../BlInputTime.vue";
import BlSelectBox from "../../../BlSelectBox.vue";

export default {
  name: "EditTargetDetailsModal",
  components: {
    BlBackdrop,
    BlTextBody2,
    BlTextSubtitle1,
    BlInputText,
    BlInputDate,
    BlInputTime,
    BlSelectBox
  },
  props: ["target", "panelId"],
  data() {
    const chipClass =
      "h-6 px-2 flex items-center rounded-full flex-shrink-0 text-xs";
    return {
      modalLoading: true,
      submitLoading: false,
      nameInput: {
        label: "Title",
        placeholder: "Target title",
        model: "",
        asterisk: true
      },
      curriculumIdInput: "",
      typeInput: "",
      typeOptions: [
        { value: "Target::Percentage", label: "Percentage" },
        { value: "Target::Duration", label: "Duration" },
        { value: "Target::Pir", label: "Partial Interval Recording" },
        { value: "Target::Frequency", label: "Frequency" },
        { value: "Target::Prompting", label: "Prompting" },
        { value: "Target::Sbt", label: "Skill-Based Treatment (SBT)" }
      ],
      statusInput: "",
      statusOptions: [
        {
          value: "pending",
          label: "Pending",
          class: `${chipClass} text-slate-800 bg-slate-300`
        },
        {
          value: "in_progress",
          label: "In acquisition",
          class: `${chipClass} text-tulip-800 bg-tulip-200`
        },
        {
          value: "paused",
          label: "Paused",
          class: `${chipClass} text-drak-purple-100 bg-light-purple-100`
        },
        {
          value: "mastered",
          label: "Mastered",
          class: `${chipClass} text-grass-800 bg-grass-200`
        },
        {
          value: "discontinued",
          label: "Discontinued",
          class: `${chipClass} text-tomato-700 bg-tomato-200`
        }
      ],
      dateIntroduceInput: {
        label: "Date introduced",
        model: ""
      },
      dateMasteredInput: {
        label: "Date mastered",
        model: "",
        disabled: true
      },
      descriptionInput: {
        label: "Description",
        placeholder: "Type the target description",
        model: "",
        textarea: true
      },
      goalInput: {
        label: "Goal",
        placeholder: "1-100",
        model: "",
        suffix: "percent (%)",
        asterisk: true
      },
      goalFrequencyInput: {
        label: "Goal",
        placeholder: "Target goal",
        model: "5",
        suffix: "attempt(s) per session",
        asterisk: true
      },
      goalPromptInput: {
        label: "Minimum attempts per session",
        placeholder: "Type goal",
        model: "2",
        suffix: "attempt(s)",
        asterisk: true
      },
      trialsInput: {
        label: "Number of trials",
        placeholder: "Target's number of trials",
        model: "",
        asterisk: true
      },
      timeInput: {
        label: "Goal time (hours/minutes/seconds)",
        model: "",
        asterisk: true
      },
      intervalInput: {
        label: "Interval",
        placeholder: "Target interval",
        model: "5",
        suffix: "Minutes",
        disabled: true
      },
      durationInput: {
        label: "Duration",
        placeholder: "Minimum 5 minutes",
        model: "5",
        suffix: "Minutes",
        asterisk: true
      },
      promptingFormatInput: "classic", // classic | custom
      successMetricInput: "",
      successMetricOptions: [
        {
          value: "equal to or greater than goal",
          label: "Equal to or greater than goal"
        },
        { value: "less than goal", label: "Less than goal" }
      ],
      defaultPrompts: []
    };
  },
  computed: {
    ...mapGetters([""]),
    isDisabledSubmit() {
      // Basic checks
      if (!this.nameInput.model) return true;
      if (!this.typeInput) return true;
      if (!this.curriculumIdInput) return true;
      // Type-specific checks
      if (this.typeInput.includes("Percentage")) {
        if (!this.successMetricInput) return true;
        if (!this.goalInput.model) return true;
        if (!this.trialsInput.model) return true;
      }
      if (this.typeInput.includes("Duration")) {
        if (!this.successMetricInput) return true;
        if (this.timeInput.model === "00:00:00") return true;
        if (!this.timeInput.model) return true;
      }
      if (this.typeInput.includes("Pir")) {
        if (!this.successMetricInput) return true;
        if (!this.goalInput.model) return true;
        if (!this.intervalInput.model) return true;
        if (!this.durationInput.model) return true;
      }
      if (this.typeInput.includes("Frequency")) {
        if (!this.successMetricInput) return true;
        if (!this.goalFrequencyInput.model) return true;
      }
      if (this.typeInput.includes("Prompting")) {
        if (!this.successMetricInput) return true;
        if (this.promptingFormatInput === "classic") {
          if (!this.goalPromptInput.model) return true;
        }
        if (this.promptingFormatInput === "custom") {
          if (!this.goalInput.model) return true;
        }
      }
      return false;
    }
  },
  watch: {
    target: {
      deep: true,
      handler(val) {
        this.onAssignInputs(val);
      }
    },
    statusInput(val) {
      if (val === "mastered") {
        const currentDateMastered = this.target.date_mastered;
        this.dateMasteredInput.model =
          currentDateMastered !== null
            ? currentDateMastered
            : moment().format("YYYY-MM-DD");
        this.dateMasteredInput.disabled = false;
      }
    }
  },
  async mounted() {
    this.onAssignInputs(this.target);
    setTimeout(() => {
      this.modalLoading = false;
    }, 300);
  },
  methods: {
    ...mapActions(["updateTarget", "showToast"]),
    onAssignInputs(target) {
      // base
      this.nameInput.model = `${target.name}`;
      this.typeInput = target.type;
      this.curriculumIdInput = target.curriculum_id;
      this.descriptionInput.model = target.description;
      this.statusInput = target.status;
      if (target.date_introduce) {
        this.dateIntroduceInput.model = moment(target.date_introduce).format(
          "YYYY-MM-DD"
        );
      } else {
        this.dateIntroduceInput.model = "";
      }
      if (target.date_mastered) {
        this.dateMasteredInput.model = moment(target.date_mastered).format(
          "YYYY-MM-DD"
        );
        this.dateMasteredInput.disabled = false;
      } else {
        this.dateMasteredInput.model = "";
        this.dateMasteredInput.disabled = true;
      }

      // percentage
      if (target.type === "Target::Percentage") {
        this.successMetricInput = target.success_metric;
        this.goalInput.model = target.goal;
        this.trialsInput.model = target.number_of_trial;
      }
      // duration
      if (target.type === "Target::Duration") {
        this.successMetricInput = target.success_metric;
        this.timeInput.model = target.goal_time;
      }
      // pir
      if (target.type === "Target::Pir") {
        this.successMetricInput = target.success_metric;
        this.goalInput.model = target.goal;
        this.intervalInput.model = target.interval;
        this.durationInput.model = target.duration;
      }
      // frequency
      if (target.type === "Target::Frequency") {
        this.successMetricInput = target.success_metric;
        this.goalFrequencyInput.model = target.goal;
      }
      // prompting
      if (target.type === "Target::Prompting") {
        this.successMetricInput = target.success_metric;
        this.promptingFormatInput = target.prompting_format;
        if (target.prompting_format === "classic") {
          this.goalPromptInput.model = target.goal;
        }
        if (target.prompting_format === "custom") {
          this.goalInput.model = target.goal;
        }
        this.defaultPrompts = target.prompts;
      }
      // sbt
      if (target.type === "Target::Sbt") {
        this.defaultPrompts = target.prompts;
      }
    },
    //
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    async onSubmit() {
      const params = {
        id: this.target.id,
        target: {
          name: this.nameInput.model,
          type: this.typeInput,
          curriculum_id: this.curriculumIdInput,
          description: this.descriptionInput.model,
          //
          status: this.statusInput,
          date_introduce: this.dateIntroduceInput.model,
          date_mastered: this.dateMasteredInput.model
        }
      };

      if (params.target.type === "Target::Percentage") {
        params.target.success_metric = this.successMetricInput;
        params.target.goal = this.goalInput.model;
        params.target.number_of_trial = this.trialsInput.model;
        if (params.target.goal > 100) {
          const title = "Invalid input";
          const message = "Goal must be less than or equal to 100 percent(%).";
          this.showToast({ title, message, type: "error" });
          return;
        }
      }

      if (this.typeInput === "Target::Duration") {
        params.target.success_metric = this.successMetricInput;
        params.target.goal_time = this.timeInput.model;
      }

      if (params.target.type === "Target::Pir") {
        params.target.success_metric = this.successMetricInput;
        params.target.goal = this.goalInput.model;
        params.target.interval = this.intervalInput.model;
        params.target.duration = this.durationInput.model;
        if (params.target.goal > 100) {
          const title = "Invalid input";
          const message = "Goal must be less than or equal to 100 percent(%).";
          this.showToast({ title, message, type: "error" });
          return;
        }
        if (params.target.duration < 5) {
          const title = "Invalid input";
          const message = "Duration must be more than or equal to 5 minutes.";
          this.showToast({ title, message, type: "error" });
          return;
        }
      }

      if (this.typeInput === "Target::Frequency") {
        params.target.success_metric = this.successMetricInput;
        params.target.goal = this.goalFrequencyInput.model;
      }

      if (this.typeInput === "Target::Prompting") {
        params.target.success_metric = this.successMetricInput;
        params.target.prompting_format = this.promptingFormatInput;
        if (this.promptingFormatInput === "classic") {
          params.target.goal = this.goalPromptInput.model;
        }
        if (this.promptingFormatInput === "custom") {
          params.target.goal = this.goalInput.model;
        }
      }

      this.submitLoading = true;
      const { success } = await this.updateTarget(params);
      this.submitLoading = false;
      if (!success) return;

      const title = "Target updated";
      const message = "Success! The target has been updated.";
      this.showToast({ title, message, type: "success" });
      this.$emit("close-modal");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/target/modal/EditTargetDetailsModal.vue